import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Loader from "./Loader";
import Sidebar from "./Sidebar";
import { toast } from "react-toastify";
import { firestore } from "../firebase.config";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  query,
  limit,
} from "firebase/firestore";

const Metrics = () => {
  const cloudName = "dabhatz7m";
  const uploadPreset = "ImageUpload";

  const [metricsDocId, setMetricsDocId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [metrics, setMetrics] = useState({
    CowsProtected: { value: "", image: "", progress: 0 },
    MonthlyFoodCost: { value: "", image: "", progress: 0 },
    MonthlyMedical: { value: "", image: "", progress: 0 },
    DedicatedStaff: { value: "", image: "", progress: 0 },
  });

  const toggleSidebar = (state) => {
    setIsSidebarOpen(state);
  };

  // ================= IMAGE UPLOAD =================
  const handleImageUpload = (file, type) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", uploadPreset);
    formData.append("folder", "Metrics");

    const xhr = new XMLHttpRequest();
    xhr.open(
      "POST",
      `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
      true
    );

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = (event.loaded / event.total) * 100;
        setMetrics((prev) => ({
          ...prev,
          [type]: { ...prev[type], progress: percent },
        }));
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        const data = JSON.parse(xhr.responseText);

        setMetrics((prev) => ({
          ...prev,
          [type]: {
            ...prev[type],
            image: data.secure_url,
            progress: 0,
          },
        }));

        toast.success(`${type} image uploaded`);
      } else {
        toast.error("Upload failed");
      }
    };

    xhr.onerror = () => {
      toast.error("Upload error");
    };

    xhr.send(formData);
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return toast.error("Please select an image");
    handleImageUpload(file, type);
  };

  const removeImage = (type) => {
    setMetrics((prev) => ({
      ...prev,
      [type]: { ...prev[type], image: "" },
    }));
  };

  // ================= FETCH =================
  const fetchMetrics = async () => {
    setIsLoading(true);

    try {
      const ref = collection(firestore, "Metrics");
      const q = query(ref, limit(1));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const document = snapshot.docs[0];
        const data = document.data();

        setMetricsDocId(document.id);

        setMetrics({
          CowsProtected: {
            value: data.CowsProtected?.value || "",
            image: data.CowsProtected?.image || "",
            progress: 0,
          },
          MonthlyFoodCost: {
            value: data.MonthlyFoodCost?.value || "",
            image: data.MonthlyFoodCost?.image || "",
            progress: 0,
          },
          MonthlyMedical: {
            value: data.MonthlyMedical?.value || "",
            image: data.MonthlyMedical?.image || "",
            progress: 0,
          },
          DedicatedStaff: {
            value: data.DedicatedStaff?.value || "",
            image: data.DedicatedStaff?.image || "",
            progress: 0,
          },
        });

        toast.success("Metrics loaded successfully");
      }
    } catch (error) {
      toast.error("Failed to load metrics");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  // ================= SAVE / UPDATE =================
  const handleSaveOrUpdate = async () => {
    const emptyField = Object.values(metrics).some(
      (item) => !item.value
    );

    if (emptyField) {
      return toast.error("All fields are required");
    }

    try {
      setIsLoading(true);

      const data = {
        CowsProtected: {
          value: metrics.CowsProtected.value,
          image: metrics.CowsProtected.image,
        },
        MonthlyFoodCost: {
          value: metrics.MonthlyFoodCost.value,
          image: metrics.MonthlyFoodCost.image,
        },
        MonthlyMedical: {
          value: metrics.MonthlyMedical.value,
          image: metrics.MonthlyMedical.image,
        },
        DedicatedStaff: {
          value: metrics.DedicatedStaff.value,
          image: metrics.DedicatedStaff.image,
        },
        updatedAt: new Date(),
      };

      if (metricsDocId) {
        await setDoc(doc(firestore, "Metrics", metricsDocId), data, {
          merge: true,
        });

        toast.success("Metrics updated successfully");
      } else {
        const newDoc = doc(collection(firestore, "Metrics"));

        await setDoc(newDoc, data);
        setMetricsDocId(newDoc.id);

        toast.success("Metrics saved successfully");
      }
    } catch (error) {
      toast.error("Failed to save metrics");
    } finally {
      setIsLoading(false);
    }
  };

  // ================= FIELD UI =================
  const renderField = (label, type) => {
    const field = metrics[type];
    const inputId = `${type}-file`;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="border p-4 rounded-xl space-y-3"
      >
        <label className="font-medium">{label}</label>

        <input
          type="number"
          value={field.value}
          onChange={(e) =>
            setMetrics((prev) => ({
              ...prev,
              [type]: { ...prev[type], value: e.target.value },
            }))
          }
          className="w-full px-4 py-2 border rounded-lg"
        />

        {/* Styled Upload Button */}
        <label
          htmlFor={inputId}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#fcaf17] to-orange-500 text-white font-medium shadow cursor-pointer hover:scale-105 transition"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7 16a4 4 0 01-.88-7.9A5 5 0 0115.9 6h.6a5 5 0 011 9.9M12 12v9m0 0l-3-3m3 3l3-3"
            />
          </svg>
          Choose Image
        </label>

        <input
          id={inputId}
          type="file"
          accept="image/*"
          onChange={(e) => handleFileChange(e, type)}
          className="hidden"
        />

        {field.progress > 0 && (
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full"
              style={{ width: `${field.progress}%` }}
            />
          </div>
        )}

        {field.image && (
          <div className="flex items-center gap-4">
            <img
              src={field.image}
              alt={label}
              className="w-24 h-24 object-cover rounded-lg"
            />
            <button
              onClick={() => removeImage(type)}
              className="text-red-600 text-sm"
            >
              Remove Image
            </button>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="lg:flex h-screen bg-white">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div className="flex-1 p-10 overflow-y-auto">
        <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-2xl p-8">
          <h1 className="text-2xl font-bold text-[#fcaf17] mb-6">
            Add Milestones & Metrics
          </h1>

          {isLoading ? (
            <Loader />
          ) : (
            <div className="space-y-6">
              {renderField("Cows Protected", "CowsProtected")}
              {renderField("Monthly Food Cost", "MonthlyFoodCost")}
              {renderField("Monthly Medical", "MonthlyMedical")}
              {renderField("Dedicated Staff", "DedicatedStaff")}

              <button
                onClick={handleSaveOrUpdate}
                className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700"
              >
                {metricsDocId ? "Update Metrics" : "Save Metrics"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Metrics;